import { useEffect, useReducer, useMemo } from 'react';
import PropTypes from 'prop-types';
import { isEmpty, get } from 'lodash';
import Jsona from 'jsona';
import {
  Button,
  Fab,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  ListItemText,
  makeStyles,
} from '@material-ui/core';
import { Add } from '@material-ui/icons';

import { getPads } from '~/clients/jsonApi';

const dataFormatter = new Jsona();

const useStyles = makeStyles({
  fabSmallBtn: {
    width: 24,
    height: 24,
    minHeight: 24,
    margin: '0 6px 0 1px',
  },
  fabSmallBtnIcon: {
    width: 20,
    height: 20,
  },
  dialogPaper: {
    width: '75%',
    maxWidth: 420,
  },
});

const assetStateReducer = (state, action) => {
  switch (action.type) {
    case 'FETCH_PADS_SUCCESS':
      return { ...state, padsData: action.payload, requestState: 'success' };
    case 'FETCH_WELLS_SUCCESS':
      return { ...state, wellsData: action.payload };
    case 'FAILURE':
      return { ...state, requestState: 'failure', error: action.payload };
    default:
      return state;
  }
};

const dialogStateReducer = (state, action) => {
  switch (action.type) {
    case 'OPEN_DIALOG':
      return {
        ...state,
        selectedPadIds: action.payload.currentPadIds,
        selectedWellIds: action.payload.currentWellIds,
        dialogState: 'open',
      };
    case 'CLOSE_DIALOG':
      return { ...state, selectedPadIds: [], selectedWellIds: [], dialogState: 'closed' };
    case 'SET_PAD_ID': {
      const currentWells = [];
      action.payload.padsData.forEach(pad => {
        if (action.payload.selectedPadIds.includes(pad.id)) {
          pad.wells.forEach(well => {
            if (!currentWells.includes(well.asset_id)) {
              currentWells.push(well.asset_id);
            }
          });
        }
      });
      return {
        ...state,
        selectedPadIds: action.payload.selectedPadIds,
        selectedWellIds: currentWells,
      };
    }
    case 'SET_WELL_ID':
      return { ...state, selectedWellIds: action.payload };
    default:
      return state;
  }
};

const PadOffsetsPicker = ({ onSave, currentOffsetSetting, assets, companyId }) => {
  const classes = useStyles();
  const [requestState, dispatchRequest] = useReducer(assetStateReducer, {
    padsData: [],
    wellsData: [],
    requestState: 'idle',
    error: null,
  });

  const [dialogState, dispatchDialog] = useReducer(dialogStateReducer, {
    selectedPadIds: [],
    selectedWellIds: [],
    dialogState: 'closed',
  });

  const currentOffsetIds = useMemo(
    () =>
      isEmpty(currentOffsetSetting) && !isEmpty(assets)
        ? assets.map(asset => asset.asset_id)
        : currentOffsetSetting.map(offset => offset.selectedWellId),
    [currentOffsetSetting, assets]
  );

  const fetchPads = () => {
    getPads({
      fields: ['pad.name', 'pad.wells'],
      sort: 'name',
      per_page: 1000,
      company: companyId,
    })
      .then(result => dataFormatter.deserialize(result))
      .then(result => result.filter(pad => !isEmpty(get(pad, 'wells'))))
      .then(filteredPads => dispatchRequest({ type: 'FETCH_PADS_SUCCESS', payload: filteredPads }))
      .catch(error => dispatchRequest({ type: 'FAILURE', payload: error }));
  };

  const fetchWells = selectedPadsData => {
    const wellsData = selectedPadsData.reduce((result, padData) => {
      return result.concat(padData.wells);
    }, []);
    dispatchRequest({ type: 'FETCH_WELLS_SUCCESS', payload: wellsData });
  };

  const handlePadSelect = event => {
    const selectedPadIds = event.target.value;
    const currentWells = [];
    requestState.padsData.forEach(pad => {
      if (selectedPadIds.includes(pad.id)) {
        pad.wells.forEach(well => {
          if (!currentWells.includes(well.asset_id)) {
            currentWells.push(well.asset_id);
          }
        });
      }
    });
    dispatchDialog({
      type: 'SET_PAD_ID',
      payload: {
        selectedPadIds,
        currentWellIds: currentOffsetIds,
        padsData: requestState.padsData,
      },
    });
  };

  const handleWellSelect = event => {
    dispatchDialog({ type: 'SET_WELL_ID', payload: event.target.value });
  };

  const closeDialog = () => dispatchDialog({ type: 'CLOSE_DIALOG' });

  const openDialog = () => {
    const currentPadIds = [];
    if (currentOffsetIds && currentOffsetIds.length > 0) {
      requestState.padsData.forEach(pad => {
        pad.wells.forEach(well => {
          if (currentOffsetIds.includes(well.asset_id) && !currentPadIds.includes(pad.id)) {
            currentPadIds.push(pad.id);
          }
        });
      });
    }
    dispatchDialog({
      type: 'OPEN_DIALOG',
      payload: {
        currentPadIds,
        currentWellIds: currentOffsetIds,
      },
    });
  };

  const handleSave = () => {
    const selectedWells = requestState.wellsData.filter(well =>
      dialogState.selectedWellIds.includes(well.asset_id)
    );

    const newOffsets = selectedWells.map(well => {
      const preSelectedWell = currentOffsetSetting.find(
        setting => setting.selectedWellId === Number(well.asset_id)
      );
      if (preSelectedWell) return preSelectedWell;

      return {
        selectedWellId: Number(well.asset_id),
        selectedWellName: well.name,
        selectedWellStatus: well.status,
        selectedStages: [],
        stages: {},
      };
    });

    closeDialog();
    onSave(newOffsets);
  };

  useEffect(() => {
    fetchPads();
  }, [companyId]);

  useEffect(() => {
    if (!isEmpty(dialogState.selectedPadIds)) {
      const selectedPadsData = requestState.padsData.filter(pad =>
        dialogState.selectedPadIds.includes(pad.id)
      );
      fetchWells(selectedPadsData);
    }
  }, [dialogState.selectedPadIds]);

  const assetIds = new Set(assets.map(asset => Number(asset.asset_id)));

  return (
    <div>
      <Fab
        size="small"
        color="primary"
        classes={{ sizeSmall: classes.fabSmallBtn }}
        onClick={openDialog}
        disabled={requestState.requestState !== 'success'}
      >
        <Add className={classes.fabSmallBtnIcon} />
      </Fab>
      {requestState.requestState === 'success' && (
        <Dialog
          open={dialogState.dialogState === 'open'}
          onBackdropClick={closeDialog}
          classes={{ paper: classes.dialogPaper }}
        >
          <DialogTitle>Offset Wells</DialogTitle>
          <DialogContent>
            <FormControl margin="normal" fullWidth required>
              <InputLabel htmlFor="pad-select" shrink={!isEmpty(dialogState.selectedPadIds)}>
                Pad
              </InputLabel>
              <Select
                multiple
                inputProps={{ name: 'pads', id: 'pad-select' }}
                value={dialogState.selectedPadIds}
                onChange={handlePadSelect}
                renderValue={selected => `${selected.length} selected`}
              >
                {requestState.padsData.map(pad => (
                  <MenuItem key={pad.id} value={pad.id}>
                    <Checkbox checked={dialogState.selectedPadIds.includes(pad.id)} />
                    <ListItemText primary={pad.name} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl
              margin="normal"
              disabled={isEmpty(dialogState.selectedPadIds)}
              fullWidth
              required
            >
              <InputLabel htmlFor="wells" shrink={!isEmpty(dialogState.selectedWellIds)}>
                Well
              </InputLabel>
              <Select
                multiple
                inputProps={{ name: 'wells', id: 'well-select' }}
                value={dialogState.selectedWellIds}
                onChange={handleWellSelect}
                renderValue={selected => `${selected.length} selected`}
              >
                {requestState.wellsData.map(well => {
                  const isMenuItemDisabled = assetIds.has(well.asset_id);
                  return (
                    <MenuItem key={well.id} value={well.asset_id} disabled={isMenuItemDisabled}>
                      <Checkbox checked={dialogState.selectedWellIds.includes(well.asset_id)} />
                      <ListItemText primary={well.name} />
                    </MenuItem>
                  );
                })}
              </Select>
            </FormControl>
          </DialogContent>
          <DialogActions>
            <Button onClick={closeDialog}>Cancel</Button>
            <Button
              variant="contained"
              color="primary"
              disabled={isEmpty(dialogState.selectedWellIds)}
              onClick={handleSave}
            >
              Save
            </Button>
          </DialogActions>
        </Dialog>
      )}
    </div>
  );
};

PadOffsetsPicker.propTypes = {
  currentOffsetSetting: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  onSave: PropTypes.func.isRequired,
  companyId: PropTypes.string.isRequired,
  assets: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
};

export default PadOffsetsPicker;
